/**
 * useEngineerPhoto(engineer) - the picture to show for an engineer: a URL for
 * an <img src>, or '' while there is none (yet).
 *
 * GET /api/engineers carries no photo bytes (10/13/26: they made the list 24
 * MB, re-read after every save). Each row says has_photo / photo_version, and
 * the picture comes from GET /api/engineers/:id/photo?v=<version>. An <img
 * src> cannot send the sign-in header every /api request needs, so the bytes
 * are fetched through the app's patched fetch (src/index.js) and shown as an
 * object URL.
 *
 * One fetch per engineer and version for the whole session, however many
 * cards, panels and forms show it (cache below). A new version - a new
 * picture - fetches again and lets the old object URL go. The browser also
 * keeps the answer (private, a year: the URL changes with the picture).
 * A photo_url that is a link rather than bytes is used as it is.
 */
import { useEffect, useState } from 'react';

const cache = new Map(); // id -> { v, url, promise }

const idOf = (e) => (e && e.id !== null && e.id !== undefined ? String(e.id) : '');
const versionOf = (e) => (e && (e.photoVersion || e.photo_version)) || '';

/** A photo the row carries itself: a link (or, from an old answer, bytes). */
export function directPhoto(e) {
  const p = e && (e.photoUrl || e.photo_url || e.photo);
  return typeof p === 'string' ? p : '';
}

/** The photo endpoint for an engineer row. */
export const photoUrlFor = (id, v) => `/api/engineers/${encodeURIComponent(id)}/photo?v=${encodeURIComponent(v)}`;

/**
 * The object URL of this engineer's picture at this version (a promise of it;
 * '' when it cannot be read). Fetched once per id + version.
 */
export function loadEngineerPhoto(id, v) {
  const key = String(id);
  const hit = cache.get(key);
  if (hit && hit.v === v) return hit.promise;
  if (hit && hit.url && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(hit.url);
  const entry = { v, url: '', promise: null };
  entry.promise = fetch(photoUrlFor(key, v))
    .then((r) => (r.ok ? r.blob() : null))
    .then((blob) => {
      if (!blob || typeof URL.createObjectURL !== 'function') return '';
      entry.url = URL.createObjectURL(blob);
      return entry.url;
    })
    .catch(() => '')
    .then((url) => {
      // a failed read is not kept: the next card that asks tries again
      if (!url && cache.get(key) === entry) cache.delete(key);
      return url;
    });
  cache.set(key, entry);
  return entry.promise;
}

/** What is already known for this engineer and version, without fetching. */
export function cachedEngineerPhoto(id, v) {
  const hit = cache.get(String(id));
  return hit && hit.v === v ? hit.url : '';
}

/** For tests: forget every picture. */
export function clearEngineerPhotoCache() {
  for (const e of cache.values()) if (e.url && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(e.url);
  cache.clear();
}

export function useEngineerPhoto(engineer) {
  const id = idOf(engineer);
  const v = versionOf(engineer);
  const direct = directPhoto(engineer);
  const wanted = !direct && !!id && !!v && (engineer.hasPhoto ?? engineer.has_photo ?? true) !== false;
  const [loaded, setLoaded] = useState(() => (wanted ? { key: `${id}@${v}`, url: cachedEngineerPhoto(id, v) } : null));

  useEffect(() => {
    if (!wanted) return undefined;
    let live = true;
    const key = `${id}@${v}`;
    const known = cachedEngineerPhoto(id, v);
    if (known) {
      setLoaded({ key, url: known });
      return undefined;
    }
    loadEngineerPhoto(id, v).then((url) => {
      if (live) setLoaded({ key, url });
    });
    return () => {
      live = false;
    };
  }, [wanted, id, v]);

  if (direct) return direct;
  if (!wanted) return '';
  // an answer for another engineer or an older picture is never shown
  return loaded && loaded.key === `${id}@${v}` ? loaded.url : cachedEngineerPhoto(id, v);
}
